import WorkspaceSecondaryButton from "@/components/SecondaryButton";
import WorkspacePrimaryButton from "@/components/PrimaryButton";
import { useRef, useState } from "react";
import LessonContentDrawer from "./LessonContentDrawer";
import { Lesson } from "@/types/tutor";
import api from "@/utils/axios-utils";
import {
  useNotificationStore,
  NotificationType,
} from "@/store/useNotificationStore";
import LessonDrawer from "@/components/workspace/LessonDrawer";
import AnalyticsLinkButton from "@/components/AnalyticsLinkButton";

const LessonCardCompact = ({
  lesson,
  index,
  classroomId,
  analyticsHref,
  onUnassign,
}: {
  lesson: Lesson;
  index: number;
  classroomId: string;
  analyticsHref?: string;
  onUnassign?: (lessonId: number) => void;
}) => {
  const { showNotification } = useNotificationStore();
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUnassign = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    setError(null);
    try {
      await api.post(
        `/classrooms/${classroomId}/lessons/${lesson.id}/unassign`,
      );
      showNotification(
        "Lesson successfully removed from the group.",
        NotificationType.SUCCESS,
      );
      setShowConfirm(false);
      onUnassign?.(lesson.id);
    } catch (err: any) {
      setError(
        err.response?.data?.detail ||
          "Failed to remove lesson from group. Please try again.",
      );
      // The dialog stays open with this inline alert; no toast on top of it.
      console.error("Error unassigning lesson:", err);
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  return (
    <>
      <tr>
        <td>
          <button
            type="button"
            className="et-group-lesson-name"
            title={lesson.name}
            onClick={() => setDetailsOpen(true)}
            aria-haspopup="dialog"
          >
            {index + 1}. {lesson.name}
          </button>
        </td>
        <td>
          <span>{lesson.assigned_by_name || "—"}</span>
        </td>
        <td>
          <div className="et-group-lesson-actions">
            {analyticsHref && (
              <AnalyticsLinkButton href={analyticsHref} subject={lesson.name} />
            )}
            <button
              type="button"
              disabled={saving}
              onClick={() => {
                setError(null);
                setShowConfirm(true);
              }}
              className="et-group-unassign"
            >
              {saving ? "Unassigning…" : "Unassign"}
            </button>
          </div>
        </td>
      </tr>
      <LessonContentDrawer
        lesson={lesson}
        isOpen={detailsOpen}
        onToggle={() => setDetailsOpen((open) => !open)}
      />
      {showConfirm && (
        <LessonDrawer
          title="Unassign lesson"
          eyebrow="Group assignment"
          closeLabel="Close unassignment"
          className="et-compact-dialog"
          onClose={() => {
            if (!inFlight.current) setShowConfirm(false);
          }}
        >
          <h2>Unassign lesson?</h2>
          <p>{lesson.name}</p>
          <p>
            You will need to assign this lesson again if you change your mind.
          </p>
          {error && (
            <p role="alert" className="et-group-error">
              {error}
            </p>
          )}
          <div className="et-dialog-actions">
            <WorkspaceSecondaryButton
              disabled={saving}
              className="et-secondary-button"
              onClick={() => setShowConfirm(false)}
            >
              Cancel
            </WorkspaceSecondaryButton>
            <WorkspacePrimaryButton
              disabled={saving}
              className="et-primary-button"
              onClick={handleUnassign}
            >
              {saving ? "Unassigning…" : "Unassign"}
            </WorkspacePrimaryButton>
          </div>
        </LessonDrawer>
      )}
    </>
  );
};

export default LessonCardCompact;
